import React, { useEffect, useState } from "react";
import {
  Grid,
  Button,
  Typography,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useParams, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import axios from "axios";
import { usePrivilegesConfig } from "../../../context/PrivilegesConfigContext";
import { MdExpandMore } from "react-icons/md";

function AddRole() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("vedhvidhi-admin-token");
  const { privilegesConfig, loading: configLoading, error: configError } =
    usePrivilegesConfig();

  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      roleName: "",
      privileges: [],
    },
  });

  useEffect(() => {
    if (id) {
      const fetchRoleDetails = async () => {
        setInitialLoading(true);
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BASEURL}/admin/role/${id}`,
            {
              headers: { Authorization: token },
            }
          );
          const role = res?.data?.data;
          reset({
            roleName: role?.roleName || "",
            privileges: role?.privileges || [],
          });
        } catch (err) {
          toast.error("Failed to load role details");
        } finally {
          setInitialLoading(false);
        }
      };
      fetchRoleDetails();
    }
  }, [id, reset, token]);

  const onSubmit = async (formData) => {
    setLoading(true);
    try {
      const apiCall = id
        ? axios.patch(
          `${process.env.REACT_APP_BASEURL}/admin/role/${id}`,
          formData,
          { headers: { Authorization: token } }
        )
        : axios.post(`${process.env.REACT_APP_BASEURL}/admin/role`, formData, {
          headers: { Authorization: token },
        });

      await apiCall;
      toast.success(`Role ${id ? "updated" : "created"} successfully!`);
      navigate("/role-management");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error while saving role");
    } finally {
      setLoading(false);
    }
  };

  const renderPrivilegeGroup = (item, field) => {
    const resource = item.module;
    const groupPermissions = item.privileges.map((p) => `${resource}:${p.key}`);
    const selectedGroupPermissions = field.value.filter((p) =>
      groupPermissions.includes(p)
    );
    const isAllSelected =
      groupPermissions.length > 0 &&
      groupPermissions.length === selectedGroupPermissions.length;
    const isSomeSelected =
      selectedGroupPermissions.length > 0 && !isAllSelected;

    const handleSelectAllChange = (e) => {
      const { checked } = e.target;
      const otherPrivileges = field.value.filter(
        (p) => !groupPermissions.includes(p)
      );
      if (checked) {
        field.onChange([...otherPrivileges, ...groupPermissions]);
      } else {
        field.onChange(otherPrivileges);
      }
    };

    return (
      <Accordion key={resource} sx={{ mb: 1 }}>
        <AccordionSummary
          expandIcon={
            <MdExpandMore
              style={{
                fontSize: "22px",
                fontWeight: "bold",
                color: "#1976d2",
              }}
            />
          }
          sx={{
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
            },
            "& .MuiTypography-root": {
              fontWeight: "600",
              fontSize: "1rem",
            },
          }}
        >
          <FormControlLabel
            label={
              <Typography
                sx={{ textTransform: "capitalize", fontWeight: "bold" }}
              >
                {item.label}
              </Typography>
            }
            control={
              <Checkbox
                sx={{
                  "& .MuiSvgIcon-root": {
                    fontSize: 22,
                  },
                }}
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={handleSelectAllChange}
                onClick={(e) => e.stopPropagation()}
              />
            }
          />
        </AccordionSummary>
        <AccordionDetails>
          <FormGroup row>
            {item.privileges.map((privilege) => {
              const privilegeString = `${resource}:${privilege.key}`;
              return (
                <FormControlLabel
                  key={privilegeString}
                  control={
                    <Checkbox
                      sx={{
                        "& .MuiSvgIcon-root": {
                          fontSize: 20,
                        },
                      }}
                      checked={field.value.includes(privilegeString)}
                      onChange={(e) => {
                        const newPrivileges = e.target.checked
                          ? [...field.value, privilegeString]
                          : field.value.filter((p) => p !== privilegeString);
                        field.onChange(newPrivileges);
                      }}
                    />
                  }
                  label={privilege.label}
                />
              );
            })}
          </FormGroup>
        </AccordionDetails>
      </Accordion>
    );
  };

  if (initialLoading || configLoading) {
    return (
      <div className="p-4" style={{ textAlign: "center" }}>
        <CircularProgress /> <Typography>Loading Data...</Typography>
      </div>
    );
  }

  if (configError) {
    return (
      <div className="p-4" style={{ textAlign: "center" }}>
        <Typography color="error">Error: {configError}</Typography>
      </div>
    );
  }

  return (
    <div style={{ padding: "24px" }}>
      <Toaster />
      <Paper elevation={3} sx={{ padding: 4 }}>
        <Typography variant="h6" gutterBottom>
          {id ? "Edit Role" : "Create Role"}
        </Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role Name"
                {...register("roleName", { required: "Role name is required" })}
                error={!!errors.roleName}
                helperText={errors.roleName?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Permissions
              </Typography>
              <Controller
                name="privileges"
                control={control}
                rules={{
                  validate: (val) =>
                    val.length > 0 || "At least one permission is required",
                }}
                render={({ field, fieldState }) => (
                  <>
                    {privilegesConfig.map((item) => {
                      if (item.children && item.children.length > 0) {
                        return (
                          <Accordion
                            key={item.module}
                            sx={{
                              mb: 1,
                              boxShadow: 3,
                              "&.Mui-expanded": { margin: "8px 0" },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <MdExpandMore
                                  style={{
                                    fontSize: "22px",
                                    fontWeight: "bold",
                                    color: "#1976d2",
                                  }}
                                />
                              }
                              sx={{
                                "& .MuiAccordionSummary-content": {
                                  alignItems: "center",
                                },
                                "& .MuiTypography-root": {
                                  fontWeight: "600",
                                  fontSize: "1rem",
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  textTransform: "capitalize",
                                  fontWeight: "bold",
                                }}
                              >
                                {item.label}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails
                              sx={{
                                p: 2,
                                borderTop: "1px solid rgba(0, 0, 0, 0.12)",
                              }}
                            >
                              {item.children.map((child) =>
                                renderPrivilegeGroup(child, field)
                              )}
                            </AccordionDetails>
                          </Accordion>
                        );
                      }
                      return renderPrivilegeGroup(item, field);
                    })}
                    {fieldState.error && (
                      <Typography color="error" variant="caption">
                        {fieldState.error.message}
                      </Typography>
                    )}
                  </>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Button type="submit" variant="contained" disabled={loading}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : id ? (
                  "Update Role"
                ) : (
                  "Create Role"
                )}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </div>
  );
}

export default AddRole;
